import { HttpErrorResponse } from '@angular/common/http';

import type { InterviewResult, InterviewResultMeta } from '@recruitee/interview-results-types';
import {
  CorrelationId,
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  EntityState,
  getEntityCollectionActions,
  pipeState,
} from '@recruitee/ngrx';

type State = EntityState<InterviewResult, { scopeMeta: InterviewResultMeta }>;

const idSelector = ({ id }: { id: number | string }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<
  InterviewResult,
  { scopeMeta: InterviewResultMeta }
>()('ViewboxInterviewResults', {
  idSelector,
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const defaultActions = getEntityCollectionActions(adapter, idSelector);
    const fetchCollection = (state: State, payload: { candidateId: number }) =>
      pipeState(state, next =>
        adapter.markAsPending(next, {
          scope: sharedInterviewResultsCandidateScope(payload.candidateId),
          entities: [],
        }),
      );
    const fetchCollectionSuccess = (
      state: State,
      payload: {
        data: InterviewResult[];
        scope: string;
      },
    ) =>
      pipeState(
        state,
        next => adapter.setMany(next, payload.data, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.map(e => e.id),
          }),
      );

    return {
      fetchCollection,
      fetchCollectionSuccess,
      createOnePessimistic: (
        state,
        payload: {
          data: { value: Partial<InterviewResult>; candidateId: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      createOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) => defaultActions.createOnePessimisticSuccess(state, payload),
      createOnePessimisticFailed: defaultActions.createOnePessimisticFailed,
      update: (state, payload: { data: { id: number; value: any }; scope?: string }) =>
        adapter.markAsPending(state),
      updateSuccess: (state, payload: { data: InterviewResult; scope?: string }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      updateFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),

      deleteOnePessimistic: (
        state,
        payload: {
          data: { id: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      deleteOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) => defaultActions.removeOnePessimisticSuccess(state, payload),
      deleteOnePessimisticFailed: defaultActions.removeOnePessimisticFailed,
    };
  },
});

export const sharedInterviewResultsCandidateScope = (candidateId: number) =>
  `candidate_${candidateId}`;

export {
  actions as ViewboxInterviewResultsActions,
  selectors as ViewboxInterviewResultsSelectors,
  reducer as ViewboxInterviewResultsReducer,
  namespace as ViewboxInterviewResultsNamespace,
};
